<template>
    <form class="col-md-10 mx-auto" @submit.prevent="onSubmit">
        <div class="loader" v-if="loading"></div>
        <div v-if="!loading">
            <div class="btn-group" role="group" aria-label="Basic example">
                <button type="button" class="btn btn-primary" @click="back">
                    <i class="fas fa-th-list"></i>
                    {{ $t('backToRegister') }}
                </button>
            </div>
            <page-title :title="$t('registers.employees.title')"></page-title>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('personalData') }}</div>
                </div>
                <div class="card-body">
                    <person :person="employee.person"
                            :requiredIdentification="false"
                            :showExtended="true"
                            :show-institution="!isNew">
                    </person>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('registers.employees.gridHeaders.workData') }}</div>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="form-group col-md-12 col-lg-6 col-xl-6">
                            <label class="font-weight-bold" :for="'manager' + _uid">
                                {{$t('registers.employees.gridHeaders.manager')}}
                            </label>
                            <dynamicDropdown :apiUrl="`/api/employee/GetEmployees`"
                                             :registerName="$t('registers.employees.title')"
                                             v-model="employee.managerId"
                                             :id="'registers.employees.gridHeaders.manager' + _uid"
                                             :name="'registers.employees.gridHeaders.manager' + _uid"
                                             :data-vv-as="$t('registers.employees.gridHeaders.manager')"
                                             :fieldName="$t('registers.employees.gridHeaders.manager')"></dynamicDropdown>
                            <span class="text-danger"
                                  v-if="errors.has('registers.employees.gridHeaders.manager' + _uid)">{{ errors.first('registers.employees.gridHeaders.manager' + _uid) }}</span>
                        </div>
                        <div class="form-group col-md-12 col-lg-6 col-xl-6">
                            <label class="font-weight-bold" :for="'directorate' + _uid">
                                {{$t('registers.employees.gridHeaders.directorate')}}
                            </label>
                            <nomenclature-dropdown
                                :load-by-parent-sort="true"
                                :parent-sort="nomenclature.Directorate"
                                v-model="employee.directorateId"
                                :fieldName="$t('registers.employees.gridHeaders.directorate')"></nomenclature-dropdown>
                        </div>
                    </div>
                    <div class="row">
                        <div class="form-group col-md-12 col-lg-6 col-xl-6">
                            <label class="font-weight-bold">{{ $t('administrativePosition.title') }}</label>
                            <dynamicDropdown :apiUrl="`/api/Dropdown/GetAdministrativePositions`"
                                             :registerName="$t('administrativePosition.title')"
                                             :filter="'contains'"
                                             v-model="employee.administrativePositionId"></dynamicDropdown>
                        </div>
                        <div class="form-group col-md-12 col-lg-6 col-xl-6">
                            <label class="font-weight-bold" :for="'workPhone' + _uid">
                                {{$t('registers.employees.gridHeaders.workPhone')}}
                            </label>
                            <input type="text" class="form-control" :id="'workPhone' + _uid"
                                   v-model="employee.workPhone"/>
                        </div>
                        <div class="form-group col-md-12 col-lg-6 col-xl-6">
                            <label class="font-weight-bold" :for="'leavingDate' + _uid">
                                {{$t('registers.employees.gridHeaders.leavingDate')}}
                            </label>
                            <div id="'leavingDate' + _uid">
                                <date-picker v-model="employee.leavingDate" :showTime="false"/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header bg-primary text-white">
                    <div class="card-title h5 m-0">{{ $t('registers.employees.activityЕvaluation') }}</div>
                </div>
                <div class="card-body">
                    <div class="row pt-2 eval-item my-2" v-for="(item, index) in evals" :key="'eval'+index">
                        <div class="col-md-12">
                            <i class="fa fa-times text-danger pointer float-right" :title="$t('remove')"
                               @click="removeEval(item._id)"></i>
                        </div>
                        <div class="col-md-4 form-group">
                            <label class="font-weight-bold" :for="'directorate' + _uid">{{$t('registers.employees.year') }}</label>
                            <select class="form-control" v-model="item.year" :name="'eval-year'+index"
                                    v-validate="'required'" :data-vv-as="$t('registers.employees.year')">
                                <option v-for="year in years" :value="year" :selected="year == item.year" :key="year">{{
                                    year }}
                                </option>
                            </select>
                            <span class="text-danger" v-if="errors.has('eval-year'+index)">{{ errors.first('eval-year'+index) }}</span>
                        </div>
                        <div class="col-md-4 form-group">
                            <label class="font-weight-bold" :for="'directorate' + _uid">{{$t('registers.employees.evaluation') }}</label>
                            <input type="text"
                                   class="form-control"
                                   v-model="item.evaluation"
                                   :name="'eval'+index"
                                   v-validate="'required'"
                                   :data-vv-as="$t('registers.employees.evaluation')"/>
                            <span class="text-danger"
                                  v-if="errors.has('eval'+index)">{{ errors.first('eval'+index) }}</span>
                        </div>
                        <div class="col-md-4 form-group">
                            <label class="font-weight-bold" :for="'directorate' + _uid">{{$t('attachment') }}</label>
                            <div>
                                <file-crud :file="item.attachment"
                                            :editable="item.attachment.id ? false : true"
                                            :showDescription="false"
											:type="item.attachment.docTypeCode">
                                </file-crud>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <button class="btn btn-primary" type="button" @click="addEvaluation">{{$t('add') }}</button>
                    </div>
                </div>
            </div>

            <AuxiliaryRegister ref="auxiliaryRegisterRef"
                               :register-id="AuxiliaryRegisters.NijEmployees"
                               register-name="Employee"
                               :entry-id="employee.auxRegisterEntityId">
            </AuxiliaryRegister>

            <div class="text-center mt-3">
                <button type="button" class="btn btn-primary btn" @click="onSubmit">
                    <i class="fas fa-save"></i>
                    {{ $t('buttons.save.btnText') }}
                </button>
            </div>
        </div>
    </form>
</template>

<script>
    import PersonComponent from '@/components/person/Person';
    import { Employee, ActivityЕvaluation } from '@/models/models';
    import { Culture, NomenclatureParentBySort, AuxiliaryRegisters } from '@/models/enums';
    import NomenclatureDropdown from '@/components/dropdown/NomenclatureDropdown';
    import DynamicDropdown from '@/components/dropdown/DynamicDropdown';
    import PageTitle from '@/components/common/PageTitle';
    import FileCrud from '@/components/common/FileCRUD';
    import AuxiliaryRegister from '@/components/dynamic/AuxiliaryRegister';
    import DatePicker from '@/components/datepicker/DateTimePicker';

    export default {
        components: {
            NomenclatureDropdown,
            dynamicDropdown: DynamicDropdown,
            person: PersonComponent,
            PageTitle,
            FileCrud,
            AuxiliaryRegister,
            DatePicker
        },
        data() {
            return {
                nomenclature: NomenclatureParentBySort,
                id: '',
                loading: false,
                positionStatisticSort: NomenclatureParentBySort.PositionStatistic,
                positionSort: NomenclatureParentBySort.Position,
                employee: new Employee(),
                years: [],
                AuxiliaryRegisters: AuxiliaryRegisters,
                transportSettingsOData: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/institution/odataDropdownModel`,
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.odata4ParameterMap
                },
                institutionSchemaModel: {
                    id: { name: 'id', from: 'id', type: 'number', nullable: false },
                    text: { name: 'text', from: 'text', type: 'string', nullable: true },
                    textEn: { name: 'textEn', from: 'textEn', type: 'textEn', nullable: true },
                }
            };
        },
        methods: {
            datasorceRequestEnd(e) {

            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            onSubmit() {
                this.$validator.validateAll().then(result => {
                    if (result) {
                        this.employee.auxRegisterModel = this.$refs.auxiliaryRegisterRef.getDynamicRegisterModelForSubmit();
                        if (this.isNew) {
                            this.create();
                        } else {
                            this.edit();
                        }
                    }
                });
            },
            back() {
                this.$router.back();
            },
            backToRegister() {
                this.$router.push('/registers/employee');
            },
            edit() {
                this.loading = true;
                let data = new FormData();

                if (this.employee.activityЕvaluations) {
                    this.employee.activityЕvaluations.forEach(item => {
                        if (!item.id) {
                            // new eval
                            data.append('files', item.attachment.content);
                            delete item.attachment.content;
                        } else {
                            if (item.isDeleted) {
                                // deleted eval
                                delete item.attachment;
                            } else if (item.attachment.content) {
                                // changed eval
                                data.append('files', item.attachment.content);
                                delete item.attachment.content;
                            } else {
                                // unchanged
                                const { id } = item.attachment;
                                item.attachment = { id };
                            }
                        }
                    });
                }

                data.append('modelData', JSON.stringify(this.employee));

                this.$http
                    .put('/api/employee', data)
                    .then(result => {
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successfulSavedData'),
                            position: 'top-center',
                            color: 'success'
                        });
                        this.backToRegister();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .then(() => { this.loading = false; });
            },
            create() {
                this.loading = true;
                let data = new FormData();
                let jsonData = { ...this.employee };

                if (jsonData.activityЕvaluations) {
                    jsonData.activityЕvaluations.forEach(item => {
                        if (item.attachment.content) {
                            // new eval
                            data.append('files', item.attachment.content);
                            delete item.attachment.content;
                        }
                    });
                }

                data.append('modelData', JSON.stringify(jsonData));

                this.$http
                    .post('/api/employee', data)
                    .then(result => {
                        this.$vs.notify({
                            title: this.$t('notifications.success'),
                            text: this.$t('notifications.successfulSavedData'),
                            position: 'top-center',
                            color: 'success'
                        });
                        this.backToRegister();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t(
                                'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                            ),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    })
                    .then(() => { this.loading = false; });
            },
            loadData() {
                this.loading = true;
                this.$http
                    .get(`/api/Employee/Get/${this.id}`)
                    .then(result => {
                        this.employee = new Employee(result.data);
                    })
                    .catch(error => {
                        // TODO:
                    })
                    .then(() => { this.loading = false; });
            },
            addEvaluation() {
                this.employee.activityЕvaluations.push(new ActivityЕvaluation());
            },
            removeEval(_id) {
                const index = this.employee.activityЕvaluations.findIndex(
                    x => x._id === _id
                );

                if (this.employee.activityЕvaluations[index].id) {
                    this.employee.activityЕvaluations[index].isDeleted = true;
                } else {
                    this.employee.activityЕvaluations.splice(index, 1);
                }
            }
        },
        mounted() {
            this.id = this.$route.params.id;
            if (this.id) {
                this.loadData();
            }

            const currentYear = new Date().getFullYear();

            for (let index = currentYear; index > currentYear - 5; index--) {
                this.years.push(index);
            }
        },
        computed: {
            isNew() {
                return !this.id;
            },
            evals() {
                return this.employee.activityЕvaluations.filter(
                    item => !item.isDeleted
                );
            },
            isEnVersion() {
                return this.$store.getters.currentCulture === Culture.EN;
            },
        }
    };
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }

    .eval-item {
        border: 1px solid lightgrey;
    }
</style>
